import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MaintenancePlanService } from '../services/maintenance-plan.service';
import type { 
  MaintenancePlan, 
  PlanFilters 
} from '../types/maintenance-plan.types';
import { useState } from 'react';

export function useMaintenancePlans(companyId: string, initialFilters: PlanFilters = {}) {
  return useMaintenancePlan(companyId, initialFilters);
}

export function useMaintenancePlanStats(companyId: string) {
  return useQuery({
    queryKey: ['maintenance-plans-stats', companyId],
    queryFn: () => MaintenancePlanService.getPlanStats(companyId),
    enabled: !!companyId,
  });
}

export function useMaintenancePlan(companyId: string, initialFilters: PlanFilters = {}) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<PlanFilters>({
    status: 'all',
    priority: 'all',
    ...initialFilters
  });

  const query = useQuery({
    queryKey: ['maintenance-plans', companyId, filters],
    queryFn: () => MaintenancePlanService.getPlans(companyId, filters),
    enabled: !!companyId,
  });

  const statsQuery = useQuery({
    queryKey: ['maintenance-plans-stats', companyId],
    queryFn: () => MaintenancePlanService.getPlanStats(companyId),
    enabled: !!companyId,
  });

  const createPlanMutation = useMutation({
    mutationFn: (data: Partial<MaintenancePlan>) => MaintenancePlanService.createPlan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-plans'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-plans-stats'] });
    },
  });

  const updatePlanMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string, updates: Partial<MaintenancePlan> }) => 
      MaintenancePlanService.updatePlan(id, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-plans'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-plans', 'detail', variables.id] });
    },
  });

  return {
    ...query,
    data: query.data?.data || [],
    plans: query.data?.data || [],
    total: query.data?.total || 0,
    stats: statsQuery.data,
    isLoadingStats: statsQuery.isLoading,
    filters,
    setFilters,
    createPlan: createPlanMutation.mutateAsync,
    updatePlan: updatePlanMutation.mutateAsync,
    isCreating: createPlanMutation.isPending,
    isUpdating: updatePlanMutation.isPending,
  };
}

export function useMaintenancePlanDetail(id?: string) {
  return useQuery({
    queryKey: ['maintenance-plans', 'detail', id],
    queryFn: () => MaintenancePlanService.getPlanDetail(id!),
    enabled: !!id,
  });
}

export function useMaintenancePlanChecklists(planId?: string) {
  return useQuery({
    queryKey: ['maintenance-plan-checklists', planId],
    queryFn: () => MaintenancePlanService.getPlanChecklists(planId!),
    enabled: !!planId,
  });
}

export function useMaintenancePlanResources(planId?: string) {
  return useQuery({
    queryKey: ['maintenance-plan-resources', planId],
    queryFn: () => MaintenancePlanService.getPlanResources(planId!),
    enabled: !!planId,
  });
}
